/**
 * Print Orders — the fulfilment queue for printed single issues and
 * standing orders.
 *
 * The daily loop this screen exists for:
 *
 *   1. charged orders accumulate as editions publish
 *   2. download the printer CSV
 *   3. send it to the printer
 *   4. mark those rows queued
 *   5. mark them shipped when the printer confirms
 *
 * Steps 2 and 4 are deliberately separate. A browser download cannot be
 * confirmed from JavaScript, so a combined "export and mark" button could
 * mark rows queued that never reached anyone — and queued is exactly the
 * status that stops you chasing them.
 *
 * This screen is also the only place a stranded or failed order surfaces at
 * all: there is no alerting and print sends no email yet, so a row that
 * needs a human is shown as needing one. See `needsAttention`.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, ApiError } from '../api/client'
import { Panel } from '../components/Panel'
import { Button } from '../components/Button'
import { PubBadge } from '../components/PubBadge'
import { PrintSettingsPanel } from '../components/PrintSettingsPanel'
import { fmtDateTime, fmtMoney } from '../lib/format'
import {
  bulkSummary,
  needsAttention,
  orderActions,
  pageWindow,
  type BulkResult,
  type BulkSummary,
  type PrintOrder,
  type PrintOrderList,
  type PrintSetting,
} from '../lib/print'

const PAGE_SIZE = 50

const PUBLICATIONS = [
  { value: '', label: 'All titles' },
  { value: 'flight_journal', label: 'Flight Journal' },
  { value: 'model_airplane_news', label: 'Model Airplane News' },
  { value: 'rc_car_action', label: 'RC Car Action' },
  { value: 'diecast', label: 'Die Cast X' },
]

// Every status the row can hold, in the order it moves through them. The
// empty option is first because "everything" is the honest default for a
// queue you are triaging rather than fulfilling.
const STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_review', label: 'In review' },
  { value: 'failed', label: 'Failed' },
  { value: 'charged', label: 'Charged — ready to print' },
  { value: 'queued', label: 'Queued with the printer' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'refunded', label: 'Refunded' },
]

const STATUS_TONE: Record<string, string> = {
  pending: 'text-paper-dim',
  in_review: 'text-amber-glow',
  failed: 'text-radar-red',
  charged: 'text-radar-green',
  queued: 'text-radar-cyan',
  shipped: 'text-radar-green',
  cancelled: 'text-paper-trace',
  refunded: 'text-paper-trace',
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  in_review: 'In review',
  failed: 'Failed',
  charged: 'Charged',
  queued: 'Queued',
  shipped: 'Shipped',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
}

export function PrintOrdersPage() {
  const qc = useQueryClient()
  const [publication, setPublication] = useState('')
  const [status, setStatus] = useState('')
  const [offset, setOffset] = useState(0)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [summary, setSummary] = useState<BulkSummary | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const params = new URLSearchParams({
    limit: String(PAGE_SIZE),
    offset: String(offset),
  })
  if (publication) params.set('publication', publication)
  if (status) params.set('status', status)

  const { data, isLoading, error } = useQuery({
    queryKey: ['print-orders', publication, status, offset],
    queryFn: () => api.get<PrintOrderList>(`/api/admin/print/orders?${params.toString()}`),
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const win = pageWindow({ total, offset, shown: items.length })

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['print-orders'] })
    setSelected(new Set())
  }

  const setFilter = (set: (v: string) => void) => (v: string) => {
    set(v)
    setOffset(0)
    setSelected(new Set())
    setSummary(null)
  }

  const bulk = useMutation({
    mutationFn: (vars: { ids: number[]; status: 'queued' | 'shipped' }) =>
      api.post<BulkResult>('/api/admin/print/orders/bulk', vars),
    onSuccess: (result, vars) => {
      setActionError(null)
      setSummary(bulkSummary(result, vars.status))
      refresh()
    },
    onError: (e) => setActionError(describe(e)),
  })

  // Rows the operator has ticked AND that the bulk endpoint would accept.
  // Selecting an ineligible row is not an error — the endpoint reports it as
  // skipped — but there is no reason to send it and be told.
  const eligible = items.filter(
    (o) => selected.has(o.id) && orderActions(o.status).bulk.allowed,
  )
  const selectableOnPage = items.filter((o) => orderActions(o.status).bulk.allowed)
  const allSelected = selectableOnPage.length > 0
    && selectableOnPage.every((o) => selected.has(o.id))

  const toggle = (id: number) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectableOnPage.map((o) => o.id)))
  }

  // The CSV takes ONE exact status and 422s outside charged/queued/shipped.
  // That single value is what keeps cancelled and refunded rows out of the
  // printer's file — there is no separate exclusion — so this must stay one
  // status and never become a set.
  const csvStatus = status === 'queued' || status === 'shipped' ? status : 'charged'
  const csvParams = new URLSearchParams({ status: csvStatus })
  if (publication) csvParams.set('publication', publication)

  const attention = items.filter((o) => needsAttention(o) !== null).length

  return (
    <div className="px-8 py-6 space-y-6">
      <header>
        <h1 className="font-serif text-3xl text-paper">Print</h1>
        <p className="label mt-1">
          Turn printed copies on per title, and fulfil the orders that come
          in. Download the CSV, send it to the printer, then mark those
          orders queued — and shipped once the printer confirms. Exporting
          and marking are separate steps on purpose: a download cannot be
          confirmed from the browser, and a queued order is one you have
          stopped chasing.
        </p>
      </header>

      <OnSalePanel />

      <Panel label="Filters" accent="none">
        <div className="flex flex-wrap items-end gap-4">
          <Select label="Title" value={publication} onChange={setFilter(setPublication)}
                  options={PUBLICATIONS} />
          <Select label="Status" value={status} onChange={setFilter(setStatus)}
                  options={STATUSES} />
          <div className="ml-auto flex items-center gap-3">
            <a href={`/api/admin/print/orders.csv?${csvParams.toString()}`}
               className="font-mono text-[11px] uppercase tracking-label px-4 py-2
                          border border-amber-glow/50 text-amber-glow
                          hover:bg-amber-wash hover:border-amber-glow">
              Download {STATUS_LABEL[csvStatus].toLowerCase()} CSV
            </a>
          </div>
        </div>
        {status !== '' && status !== csvStatus && (
          <p className="label mt-3 text-paper-dim">
            The printer CSV only carries charged, queued or shipped orders, so
            it will download {STATUS_LABEL[csvStatus].toLowerCase()} rows
            regardless of the filter above.
          </p>
        )}
      </Panel>

      {attention > 0 && (
        <Panel label="Needs you" accent="red">
          <p className="label">
            {attention} {attention === 1 ? 'order needs' : 'orders need'} a
            decision on this page — a charge the gateway never confirmed, one
            that failed, or one the charge sweep gave up on. Nothing else
            reports these: print sends no email and there is no alert.
          </p>
        </Panel>
      )}

      {summary && (
        <Panel label="Last action"
               accent={summary.tone === 'ok' ? 'green' : summary.tone === 'warn' ? 'amber' : 'red'}>
          <p className="text-paper text-[13px]">{summary.message}</p>
          {summary.skipped.length > 0 && (
            <ul className="mt-2 space-y-1">
              {summary.skipped.map((line) => (
                <li key={line} className="label text-paper-dim">{line}</li>
              ))}
            </ul>
          )}
        </Panel>
      )}

      {actionError && (
        <Panel label="That did not work" accent="red">
          <p className="text-radar-red text-[13px]">{actionError}</p>
        </Panel>
      )}

      <Panel
        label={total === 0 ? 'Orders' : `Orders — ${win.label}`}
        accent="amber"
        bodyClassName="p-0"
        right={
          <>
            <Button size="sm" variant="outline" disabled={eligible.length === 0 || bulk.isPending}
                    onClick={() => bulk.mutate({
                      ids: eligible.map((o) => o.id), status: 'queued',
                    })}>
              Mark {eligible.length || ''} queued
            </Button>
            <Button size="sm" variant="outline" disabled={eligible.length === 0 || bulk.isPending}
                    onClick={() => bulk.mutate({
                      ids: eligible.map((o) => o.id), status: 'shipped',
                    })}>
              Mark {eligible.length || ''} shipped
            </Button>
          </>
        }
      >
        {isLoading && <p className="label p-4">Loading…</p>}
        {error instanceof Error && <p className="text-radar-red p-4">{error.message}</p>}

        {!isLoading && items.length === 0 && (
          <p className="label p-4">
            {total === 0
              ? 'No print orders yet. Orders appear here when a member buys a '
                + 'printed issue, or when an edition publishes and a standing '
                + 'order is charged for it.'
              : 'Nothing on this page — try going back.'}
          </p>
        )}

        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[12px]">
              <thead>
                <tr className="border-b border-ink-500/60">
                  <th className="label px-3 py-2">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll}
                           disabled={selectableOnPage.length === 0}
                           aria-label="Select every fulfillable order on this page" />
                  </th>
                  <th className="label px-3 py-2">Ordered</th>
                  <th className="label px-3 py-2">Title / edition</th>
                  <th className="label px-3 py-2">Ship to</th>
                  <th className="label px-3 py-2">Price</th>
                  <th className="label px-3 py-2">Status</th>
                  <th className="label px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {items.map((o) => (
                  <OrderRow key={o.id} order={o} checked={selected.has(o.id)}
                            onToggle={() => toggle(o.id)}
                            onDone={refresh}
                            onError={(m) => setActionError(m)} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-ink-500/60 px-4 py-2">
          <span className="label">{win.label}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" disabled={!win.hasPrev}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              Previous
            </Button>
            <Button size="sm" variant="ghost" disabled={!win.hasNext}
                    onClick={() => setOffset(offset + PAGE_SIZE)}>
              Next
            </Button>
          </div>
        </div>
      </Panel>
    </div>
  )
}

/**
 * The switch, on the screen named after the feature.
 *
 * It also lives under Settings → Payments, next to the other operator
 * settings, and both render the same component against the same query key
 * so a save in one updates the other. This copy exists because Settings is
 * where you put a setting, and this is where you go looking for it — the
 * first version had it only in Settings, more than a screen of scrolling
 * down, and it could not be found.
 */
function OnSalePanel() {
  const { data } = useQuery({
    queryKey: ['settings', 'print'],
    queryFn: () => api.get<{ items: PrintSetting[] }>('/api/admin/settings/print'),
  })
  const live = (data?.items ?? []).filter((s) => s.enabled)
  const waiting = (data?.items ?? []).reduce((n, s) => n + s.interest_count, 0)
  // Of `waiting`, how many a launch email would reach for the first time —
  // the same number PrintSettingsPanel shows per title, summed here because
  // this banner is itself a sum across titles.
  const waitingEmailOnly = (data?.items ?? []).reduce((n, s) => n + s.interest_email_only, 0)

  return (
    <Panel
      label="On sale"
      accent={live.length > 0 ? 'green' : 'amber'}
      right={
        <span className="label">
          {data ? `${live.length} of ${data.items.length} titles` : '—'}
        </span>
      }
    >
      {data && live.length === 0 && (
        <p className="label mb-1 text-amber-glow">
          Print is not on sale for any title. Members see “Coming soon” with a
          notify-me
          {waiting > 0 && (
            <>
              {` — ${waiting} ${waiting === 1 ? 'person has' : 'people have'} asked to be told`}
              {waitingEmailOnly > 0 && ` (${waitingEmailOnly} not members)`}
            </>
          )}
          . Tick a title below and save to start selling it.
        </p>
      )}
      <PrintSettingsPanel embedded />
    </Panel>
  )
}


function OrderRow({ order, checked, onToggle, onDone, onError }: {
  order: PrintOrder
  checked: boolean
  onToggle: () => void
  onDone: () => void
  onError: (message: string) => void
}) {
  const [open, setOpen] = useState(false)
  const actions = orderActions(order.status)
  const attention = needsAttention(order)

  return (
    <>
      <tr className="border-b border-ink-500/30 align-top">
        <td className="px-3 py-2">
          <input type="checkbox" checked={checked} onChange={onToggle}
                 disabled={!actions.bulk.allowed}
                 title={actions.bulk.allowed ? undefined : actions.bulk.reason}
                 aria-label={`Select order ${order.id}`} />
        </td>
        <td className="px-3 py-2 font-mono text-paper-dim whitespace-nowrap">
          {fmtDateTime(order.created_at)}
        </td>
        <td className="px-3 py-2">
          <PubBadge slug={order.publication_slug} />
          <div className="text-paper">{order.edition}</div>
          <div className="label">
            {order.kind === 'standing' ? 'Standing order' : 'Single issue'}
            {order.is_gift && ' · Gift'}
          </div>
        </td>
        <td className="px-3 py-2">
          <Link to={`/subscribers/${order.subscriber_id}`}
                className="text-amber-glow hover:underline">
            {order.ship_name}
          </Link>
          <div className="label">
            {order.ship_city}
            {order.ship_region && `, ${order.ship_region}`} {order.ship_postal}
            {order.ship_country !== 'US' && (
              <span className="text-radar-cyan"> · {order.ship_country}</span>
            )}
          </div>
          {/* Not derivable from anything else in this row — a member's own
              order can carry no subscription_id. Guest called out in color
              because it is the exception: who to chase about a guest order
              is a different question (no account) than about a member's.
              `buyer_kind` is computed per PUBLICATION — it says nothing
              about any OTHER title this buyer might hold, so the cell must
              not claim more than that. The title attribute carries the
              precise meaning; the word alone must not overclaim it. */}
          <div className="label">
            {order.buyer_kind === 'guest'
              ? <span className="text-radar-cyan" title="No subscription to this title">
                  Guest
                </span>
              : <span title="Holds a subscription to this title">Member</span>}
          </div>
        </td>
        <td className="px-3 py-2 font-mono whitespace-nowrap">
          {fmtMoney(order.price_cents / 100)}
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          <span className={`font-mono uppercase text-[11px] ${STATUS_TONE[order.status] ?? ''}`}>
            {STATUS_LABEL[order.status] ?? order.status}
          </span>
          {order.charge_attempts > 1 && (
            <div className="label">{order.charge_attempts} attempts</div>
          )}
        </td>
        <td className="px-3 py-2 text-right whitespace-nowrap">
          <Button size="sm" variant="ghost" onClick={() => setOpen(!open)}>
            {open ? 'Close' : 'Manage'}
          </Button>
        </td>
      </tr>

      {attention && (
        <tr className="border-b border-ink-500/30">
          <td />
          <td colSpan={6} className="px-3 pb-2">
            <p className="label text-radar-red">{attention}</p>
          </td>
        </tr>
      )}

      {open && (
        <tr className="border-b border-ink-500/60 bg-ink-800/40">
          <td />
          <td colSpan={6} className="px-3 py-3">
            <OrderManage order={order} onDone={onDone} onError={onError}
                         onClose={() => setOpen(false)} />
          </td>
        </tr>
      )}
    </>
  )
}

function OrderManage({ order, onDone, onError, onClose }: {
  order: PrintOrder
  onDone: () => void
  onError: (message: string) => void
  onClose: () => void
}) {
  const actions = orderActions(order.status)
  const [transId, setTransId] = useState('')
  const [reason, setReason] = useState('')

  const markPaid = useMutation({
    mutationFn: () => api.post('/api/admin/print/orders/' + order.id + '/mark_paid',
                               { gateway_trans_id: transId.trim() }),
    onSuccess: () => { onClose(); onDone() },
    onError: (e) => onError(describe(e)),
  })

  const cancel = useMutation({
    mutationFn: () => api.post('/api/admin/print/orders/' + order.id + '/cancel',
                               { reason: reason.trim() }),
    onSuccess: () => { onClose(); onDone() },
    onError: (e) => onError(describe(e)),
  })

  return (
    <div className="space-y-4">
      <div className="grid gap-1">
        <span className="label">Full address</span>
        <p className="text-paper text-[12px] font-mono leading-relaxed">
          {order.ship_name}<br />
          {order.ship_line1}<br />
          {order.ship_line2 && <>{order.ship_line2}<br /></>}
          {order.ship_city}{order.ship_region && `, ${order.ship_region}`} {order.ship_postal}<br />
          {order.ship_country}
        </p>
      </div>

      {order.is_gift && order.gift_message && (
        <div className="grid gap-1">
          <span className="label">Gift message</span>
          <p className="text-paper text-[12px] italic">“{order.gift_message}”</p>
        </div>
      )}

      {order.last_error && (
        <div className="grid gap-1">
          <span className="label">Last gateway result</span>
          <p className="font-mono text-[11px] text-paper-dim">{order.last_error}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-6">
        <div className="grid gap-2">
          <span className="label">Record a payment taken elsewhere</span>
          {actions.markPaid.allowed ? (
            <div className="flex items-end gap-2">
              <input value={transId} onChange={(e) => setTransId(e.target.value)}
                     placeholder="Authorize.Net transaction id"
                     className="bg-ink-900 border border-ink-500 px-2 py-1.5 font-mono
                                text-[12px] text-paper w-64" />
              <Button size="sm" disabled={!transId.trim() || markPaid.isPending}
                      onClick={() => markPaid.mutate()}>
                Mark paid
              </Button>
            </div>
          ) : (
            <Disabled reason={actions.markPaid.reason} label="Mark paid" />
          )}
        </div>

        <div className="grid gap-2">
          <span className="label">Stop this order</span>
          {actions.cancel.allowed ? (
            <div className="flex items-end gap-2">
              <input value={reason} onChange={(e) => setReason(e.target.value)}
                     placeholder="Why — this is kept on the order"
                     className="bg-ink-900 border border-ink-500 px-2 py-1.5 font-mono
                                text-[12px] text-paper w-64" />
              <Button size="sm" variant="danger"
                      disabled={!reason.trim() || cancel.isPending}
                      onClick={() => cancel.mutate()}>
                Cancel order
              </Button>
            </div>
          ) : (
            <Disabled reason={actions.cancel.reason} label="Cancel order" />
          )}
        </div>

        <div className="grid gap-2">
          <span className="label">Refund</span>
          <Disabled reason={actions.refund.reason} label="Refund" />
        </div>
      </div>
    </div>
  )
}

/** A control that exists so the operator knows where to go instead. */
function Disabled({ reason, label }: { reason: string; label: string }) {
  return (
    <div className="max-w-sm">
      <Button size="sm" variant="ghost" disabled title={reason}>{label}</Button>
      <p className="label mt-1 text-paper-trace">{reason}</p>
    </div>
  )
}

function Select({ label, value, onChange, options }: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <label className="grid gap-1">
      <span className="label">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="bg-ink-900 border border-ink-500 px-2 py-1.5 font-mono
                         text-[12px] text-paper">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  )
}

/** An ApiError's detail in words, rather than "[object Object]". */
function describe(e: unknown): string {
  if (e instanceof ApiError) {
    const d = e.detail
    if (typeof d === 'string') return d
    if (d && typeof d === 'object' && 'detail' in d) return String((d as { detail: unknown }).detail)
    return `The API refused that (${e.status}).`
  }
  return e instanceof Error ? e.message : 'Something went wrong.'
}
